import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Building2, Globe } from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { Input, Select, cn } from '@personic/shared'
import { IndustrySelector } from '@/components/onboarding/IndustrySelector'

const COMPANY_SIZES = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '500+', label: '500+ employees' },
]

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'JP', label: 'Japan' },
  { value: 'SG', label: 'Singapore' },
  { value: 'IN', label: 'India' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'SE', label: 'Sweden' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'IL', label: 'Israel' },
  { value: 'KR', label: 'South Korea' },
  { value: 'OTHER', label: 'Other' },
]

export default function OrganizationCreation() {
  const { organizationData, setOrganizationData, setStep } = useOnboardingStore()

  // Ensure we're on step 3
  useEffect(() => {
    setStep(3)
  }, [setStep])

  const handleChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setOrganizationData({ [field]: e.target.value })
  }

  const handleIndustryChange = (industry: string) => {
    setOrganizationData({ industry })
  }

  const handleSizeChange = (size: string) => {
    setOrganizationData({ companySize: size })
  }

  const isComplete =
    organizationData.companyName &&
    organizationData.industry &&
    organizationData.companySize &&
    organizationData.country

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-midnight-900 mb-2">
          Tell us about your organization
        </h1>
        <p className="text-warm-600">
          This helps us customize Personic AI for your specific needs.
        </p>
      </motion.div>

      {/* Form Fields */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-6"
      >
        {/* Company Name & Website */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Company Name"
            value={organizationData.companyName}
            onChange={handleChange('companyName')}
            placeholder="Acme Inc."
            required
            leftIcon={<Building2 size={18} />}
          />

          <Input
            label="Website"
            type="url"
            value={organizationData.website}
            onChange={handleChange('website')}
            placeholder="https://example.com"
            leftIcon={<Globe size={18} />}
            hint="Optional"
          />
        </div>

        {/* Industry Selector */}
        <div>
          <label className="block text-sm font-heading font-medium text-midnight-700 mb-3">
            Industry <span className="text-red-500">*</span>
          </label>
          <IndustrySelector
            value={organizationData.industry}
            onChange={handleIndustryChange}
          />
        </div>

        {/* Company Size */}
        <div>
          <label className="block text-sm font-heading font-medium text-midnight-700 mb-3">
            Company Size <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {COMPANY_SIZES.map((size) => {
              const isSelected = organizationData.companySize === size.value
              return (
                <motion.button
                  key={size.value}
                  type="button"
                  onClick={() => handleSizeChange(size.value)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    'px-4 py-3 rounded-xl border-2 transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-copper-400 focus:ring-offset-2',
                    isSelected
                      ? 'bg-copper-50 border-copper-500 text-copper-700'
                      : 'bg-white border-warm-200 hover:border-copper-300 text-midnight-700'
                  )}
                >
                  <span className="text-sm font-heading font-medium">
                    {size.label}
                  </span>
                </motion.button>
              )
            })}
          </div>
        </div>

        {/* Country */}
        <div className="max-w-xs">
          <Select
            label="Country"
            value={organizationData.country}
            onChange={handleChange('country')}
            options={[{ value: '', label: 'Select a country...' }, ...COUNTRIES]}
          />
        </div>
      </motion.div>

      {/* Validation Hint */}
      {!isComplete && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 text-sm text-warm-500 text-center"
        >
          Please fill in all required fields to continue
        </motion.p>
      )}
    </div>
  )
}
